import { connect } from 'react-redux';
import Router from 'next/router';

import { Locales } from 'types/locale';

import Article, { Roles } from 'models/Article';
import City from 'models/City';
import CmsPage from 'models/CmsPage';
import Job from 'models/Job';

import { getLocale } from 'utils/locale';
import { changeToCent } from 'utils/math';

import { list as listArticles } from 'requests/articles';
import { list as listCities, single as singleCityBySlug, singleByShortSlug } from 'requests/cities';
import { single as singleCmsPage } from 'requests/cmsPages';
import { list as listJobAgreements } from 'requests/jobAgreements';
import { list as listJobCategories } from 'requests/jobCategories';
import { list as listJobExperiences } from 'requests/jobExperiences';
import { list as listJobLanguages } from 'requests/jobLanguages';
import { list as listJobModes } from 'requests/jobModes';
import { list as listJobs } from 'requests/jobs';
import { list as listJobTimes } from 'requests/jobTimes';
import { State as ReduxState } from 'store/state';

import Component from './component';
import { IInitialResources, NextPageContextCustom, Props } from './types';

Component.getInitialProps = async (appContext: NextPageContextCustom): Promise<Props> => {
    let initialResources: IInitialResources = null;
    const salaryNumber = Number(appContext.query?.salaryFrom);
    let cmsPage: CmsPage = null;
    const getJobCityBySlugType = () => {
        const jobCompanyCitySlug = appContext.query?.jobCompanyCitySlug;
        const jobCompanyCityShortSlug = appContext.query?.jobCompanyCityShortSlug;

        if (jobCompanyCitySlug) {
            return singleCityBySlug({ slug: jobCompanyCitySlug });
        }

        if (jobCompanyCityShortSlug) {
            return singleByShortSlug({ slug: jobCompanyCityShortSlug });
        }

        return null;
    };

    await Promise.all([

        listArticles({ page: 1, perPage: 6, role: Roles.Worker }),
        listJobs({
            page: 1, perPage: 10,
            ...appContext?.query,
            salaryFrom: salaryNumber > 0 ? changeToCent(salaryNumber) : null,

            jobCategorySlug: appContext?.query?.jobCategorySlug?.toString().split(','),
            jobAgreementSlug: appContext?.query?.jobAgreementSlug?.toString().split(','),
            jobModeSlug: appContext?.query?.jobModeSlug?.toString().split(','),
            jobExperienceSlug: appContext?.query?.jobExperienceSlug?.toString().split(','),
            jobLanguageSlug: appContext?.query?.jobLanguageSlug?.toString().split(','),
            jobTimeSlug: appContext?.query?.jobTimeSlug?.toString().split(','),

        }),

        listJobModes({ page: 1, perPage: 99 }),
        listJobCategories({ page: 1, perPage: 99 }),
        listJobAgreements({ page: 1, perPage: 99 }),
        listJobExperiences({ page: 1, perPage: 99 }),
        listJobLanguages({ page: 1, perPage: 99 }),
        listJobTimes({ page: 1, perPage: 99 }),
        listCities({ page: 1, perPage: 7, promoted: true }),

        getJobCityBySlugType(),

    ]).then(responses => {
        initialResources = {
            articles: responses[0].elements,
            jobs: responses[1],
            jobModes: responses[2].elements,
            jobCategories: responses[3].elements,
            jobAgreements: responses[4].elements,
            jobExperiences: responses[5].elements,
            jobLanguages: responses[6].elements,
            jobTimes: responses[7].elements,
            jobCities: responses[8].elements,
            initialCity: responses[9],
        };
    });

    const requestedPage = Number(appContext?.query?.page) || 1;
    const lastPage = initialResources?.jobs?.meta?.lastPage || 1;

    if (requestedPage > lastPage) {
        const rawUrl = appContext.req?.url || appContext.asPath || '';
        const [urlPath, urlQuery = ''] = rawUrl.split('?');
        const urlSearchParams = new URLSearchParams(urlQuery);

        if (lastPage > 1) {
            urlSearchParams.set('page', lastPage.toString());
        } else {
            urlSearchParams.delete('page');
        }

        const queryString = urlSearchParams.toString();
        const redirectUrl = queryString ? `${urlPath}?${queryString}` : urlPath;

        if (appContext.res) {
            appContext.res.writeHead(302, { location: redirectUrl });
            appContext.res.end();
        } else {
            Router.replace(redirectUrl);
        }
    }
    try {
        await Promise.all([
            singleCmsPage({ slug: 'jobs' }),
        ]).then(responses => {
            cmsPage = responses[0];
        });
    } catch (error) {
        console.error(error);
    }

    const lang = appContext.lang || Locales.Pl;
    const locale = getLocale(lang);

    return {
        lang,
        data: locale.jobs,
        initialSearchParams: appContext?.query,
        initialResources,
        query: appContext?.query,
        cmsPage: cmsPage || null,
    };
};

const mapStateToProps = (state: ReduxState, ownProps?: Props): object => {
    return {
        state: {
            user: state.user.profile,
        },
    };
};

Component.defaultProps = {};

export default connect(mapStateToProps, null)(Component);
